import { useLang } from "@/App";
import { UI, tr, COUNTRY_NAMES, REASONS, QUESTIONS } from "@/i18n";
import { Check, ShieldCheck } from "lucide-react";

const optionLabel = (qid, value, lang) => {
  const q = QUESTIONS.find((x) => x.id === qid);
  if (!q) return value;
  const opt = (q.options || q.items || []).find((o) => o.value === value);
  return opt ? tr(lang, opt) : value;
};

// High-level profile insights built ONLY from answers the user actually provided.
const buildInsights = (answers, lang) => {
  const out = [];
  const add = (label, val) => val && out.push({ label, val });
  if (answers.profession) add(tr(lang, { en: "Work situation", tr: "Çalışma durumu", es: "Situación laboral" }), optionLabel("profession", answers.profession, lang));
  if (answers.household) add(tr(lang, { en: "Family profile", tr: "Aile profili", es: "Perfil familiar" }), optionLabel("household", answers.household, lang));
  if (answers.monthlyIncome) add(tr(lang, { en: "Income range", tr: "Gelir aralığı", es: "Rango de ingresos" }), optionLabel("monthlyIncome", answers.monthlyIncome, lang));
  const goal = (answers.immigrationGoals || [])[0];
  if (goal) add(tr(lang, { en: "Relocation objective", tr: "Taşınma hedefi", es: "Objetivo de traslado" }), optionLabel("immigrationGoals", goal, lang));
  if (answers.environment) add(tr(lang, { en: "Preferred environment", tr: "Tercih edilen ortam", es: "Entorno preferido" }), optionLabel("environment", answers.environment, lang));
  if (answers.climate) add(tr(lang, { en: "Climate preference", tr: "İklim tercihi", es: "Preferencia climática" }), optionLabel("climate", answers.climate, lang));
  return out;
};

const PreliminaryReport = ({ assessment, firstName }) => {
  const { lang } = useLang();
  const top3 = assessment.topMatches || [];
  const primary = top3[0];
  const insights = buildInsights(assessment.answers || {}, lang);
  if (!primary) return null;

  return (
    <div data-testid="preliminary-report">
      <div className="inline-flex items-center gap-2 text-xs font-semibold text-navy bg-gold/20 rounded-full px-3 py-1 mb-4">
        <ShieldCheck className="w-3.5 h-3.5" /> {tr(lang, UI.report_unlocked)}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl text-navy-deep">{tr(lang, UI.report_head)}</h1>

      {/* A. Top match */}
      <div className="mt-8 bg-navy-deep text-white rounded-sm p-6 sm:p-8 relative om-grain">
        <p className="overline text-gold mb-1">{tr(lang, UI.report_top)}</p>
        <h2 className="font-display text-3xl sm:text-4xl">{primary.flag} {tr(lang, COUNTRY_NAMES[primary.code]) || primary.name} — {primary.overall}%</h2>
        <ul className="mt-4 space-y-1.5">
          {(primary.whyCodes || []).slice(0, 3).map((c) => (
            <li key={c} className="text-sm text-white/85 flex gap-2"><Check className="w-4 h-4 text-gold shrink-0 mt-0.5" />{tr(lang, REASONS[c])}</li>
          ))}
        </ul>
      </div>

      {/* B. Top 3 */}
      <h2 className="font-display text-2xl sm:text-3xl text-navy-deep mt-12 mb-5">{tr(lang, UI.report_top3)}</h2>
      <div className="space-y-4">
        {top3.map((m, i) => (
          <div key={m.code} data-testid={`report-country-${m.code}`} className="bg-white border border-border rounded-sm p-5 sm:p-6">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="font-display text-2xl text-navy-deep"><span className="text-gold font-bold text-lg mr-2 font-sans">#{i + 1}</span>{m.flag} {tr(lang, COUNTRY_NAMES[m.code]) || m.name}</h3>
              <span className="font-display text-2xl text-navy">{m.overall}%</span>
            </div>
            <ul className="mt-3 space-y-1.5">
              {(m.whyCodes || []).slice(0, 3).map((c) => (
                <li key={c} className="text-sm text-navy-deep flex gap-2"><span className="text-gold">•</span>{tr(lang, REASONS[c])}</li>
              ))}
            </ul>
            {(m.mayNotCodes || [])[0] && (
              <p className="mt-3 text-sm text-muted-foreground"><span className="font-semibold text-navy-deep">{tr(lang, UI.consideration)}: </span>{tr(lang, REASONS[m.mayNotCodes[0]])}</p>
            )}
          </div>
        ))}
      </div>

      {/* C. Key profile insights */}
      {insights.length > 0 && (
        <div className="mt-12">
          <h2 className="font-display text-2xl sm:text-3xl text-navy-deep mb-5">{tr(lang, UI.key_insights)}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {insights.map((it, i) => (
              <div key={i} className="bg-cream border border-border rounded-sm px-4 py-3">
                <p className="overline text-muted-foreground">{it.label}</p>
                <p className="text-navy-deep font-medium">{it.val}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-8 text-sm text-navy-deep bg-cream border border-border rounded-sm p-4 leading-relaxed">{tr(lang, UI.boundary_text)}</p>
    </div>
  );
};

export default PreliminaryReport;
